/**
 * Inline SVG icon set.
 *
 * The app used emoji for tab, shop and quest glyphs. An emoji is not a design
 * decision — it is whatever face the operating system happens to ship, so the
 * same "sword" is a different shape, weight and colour on every platform, in
 * colours this palette never chose, and it ignores `currentColor` entirely.
 * That last part is the practical problem: the tab bar tints its active item
 * gold by setting a text colour, which an emoji silently refuses.
 *
 * No dependency is needed for this. The shell's sign-out control was already a
 * hand-written inline SVG, and `frontend/public/payment-methods/` is 87 more;
 * this module just makes that the rule instead of the exception.
 *
 * Conventions, all load-bearing:
 *  - 24x24 viewBox, `stroke="currentColor"`, `fill="none"` — the lucide
 *    geometry the existing sign-out glyph already follows, so the set is
 *    visually consistent with what shipped.
 *  - sized in `em`, so an icon matches whatever `font-size` its container sets
 *    and callers never pass a size.
 *  - `aria-hidden` is set here, not by the caller: every icon in this app sits
 *    beside a real text label, so none of them should reach a screen reader.
 *    Where the label was carried by the emoji itself, the call site adds an
 *    `sr-only` span instead.
 *  - `data-icon` names the glyph, so a test can assert which icon rendered.
 *    An emoji could be matched in `textContent`; an SVG has none.
 */

export type IconName =
  // ─── tab bar ───
  | 'map'
  | 'colony'
  | 'battle'
  | 'profile'
  | 'leaderboard'
  | 'quests'
  | 'shop'
  | 'settings'
  // ─── currencies & resources ───
  | 'deltaV'
  | 'chit'
  | 'regolith'
  | 'waterIce'
  // ─── quests & achievements ───
  | 'wall'
  | 'research'
  | 'medal'
  | 'coins'
  | 'gem'
  | 'star'
  // ─── offers & boosts ───
  | 'hammer'
  | 'troops'
  | 'pickaxe'
  | 'shield'
  | 'crosshair'
  | 'offerTag'
  // ─── chat ───
  | 'chat'
  // ─── ui chrome ───
  | 'externalLink'
  | 'menu'
  | 'close'
  | 'check'
  | 'clock'
  | 'skull'
  | 'crate'
  | 'filePdf'
  | 'download'
  // ─── audio ───
  | 'soundOn'
  | 'soundOff';

/** Path geometry only — the wrapper below supplies the shared attributes. */
const PATHS: Record<IconName, string> = {
  map: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="m12 7 3 5-3 5-3-5z"/>',
  colony: '<path d="M3 21h18"/><path d="M5 21V8l5-4 5 4v13"/><path d="M15 21v-7h4v7"/><path d="M9 21v-4h2v4"/>',
  battle:
    '<path d="M14.5 17.5 3 6V3h3l11.5 11.5"/><path d="m13 19 6-6"/><path d="m16 16 4 4"/><path d="M19 21l2-2"/><path d="M9.5 6.5 21 18v3h-3L6.5 9.5"/>',
  profile: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  quests: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/>',
  shop: '<circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 3h3l2.7 11.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L22 7H6"/>',
  settings:
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>',

  // A booster rising on its plume — Delta-V is "shipments from outside".
  deltaV:
    '<path d="M12 2c2.5 2.3 4 5.6 4 9 0 2.2-.6 3.9-1.3 5H9.3C8.6 14.9 8 13.2 8 11c0-3.4 1.5-6.7 4-9z"/><circle cx="12" cy="10" r="1.5"/><path d="M9.3 16 7 19l2.6-.8M14.7 16 17 19l-2.6-.8"/><path d="M11 19.5h2"/>',
  chit: '<rect x="2.5" y="6" width="19" height="12" rx="2"/><path d="M8 6v12"/><path d="M12.5 10h5M12.5 14h3"/>',
  regolith: '<path d="m4 14 4-7 5 3 3-4 4 8z"/><path d="M3 18h18"/><circle cx="8.5" cy="11.5" r=".6"/><circle cx="15" cy="12.5" r=".6"/>',
  waterIce: '<path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9"/><path d="m9.5 5 2.5 2 2.5-2M9.5 19l2.5-2 2.5 2"/>',

  wall: '<rect x="3" y="5" width="18" height="5"/><rect x="3" y="14" width="18" height="5"/><path d="M8 5v5M16 5v5M12 14v5"/>',
  research: '<path d="M9 3h6"/><path d="M10 3v6.5L5.5 18A2 2 0 0 0 7.3 21h9.4a2 2 0 0 0 1.8-3L14 9.5V3"/><path d="M7.5 15h9"/>',
  medal: '<circle cx="12" cy="14" r="6"/><path d="m8 3 2.5 5M16 3l-2.5 5"/><path d="m12 11.5 1 2 2.2.3-1.6 1.6.4 2.2-2-1-2 1 .4-2.2L8.8 13.8 11 13.5z"/>',
  coins: '<ellipse cx="9" cy="7" rx="6" ry="3"/><path d="M3 7v4c0 1.7 2.7 3 6 3s6-1.3 6-3V7"/><path d="M9 14c-3.3 0-6 1.3-6 3v0c0 1.7 2.7 3 6 3s6-1.3 6-3"/><path d="M15 11c3.3 0 6 1.3 6 3v3c0 1.7-2.7 3-6 3"/>',
  gem: '<path d="M6 3h12l3 6-9 12L3 9z"/><path d="M3 9h18M9 3 6 9l6 12 6-12-3-6"/>',
  star: '<path d="m12 3 2.7 5.6 6.3.9-4.5 4.4 1 6.1-5.5-2.9-5.5 2.9 1-6.1L3 9.5l6.3-.9z"/>',
  // Podium bars of rising rank, tallest in the middle — a ranking glyph, not a chart.
  leaderboard: '<path d="M3 20h18"/><path d="M6 20v-6"/><path d="M12 20V5"/><path d="M18 20v-9"/>',

  hammer: '<path d="m14 4 6 6-3 3-6-6z"/><path d="m11 7-1.5-1.5L12 3l5 5-2.5 2.5"/><path d="m10.5 10.5-7 7a2 2 0 0 0 3 3l7-7"/>',
  troops: '<circle cx="9" cy="8" r="3"/><path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1"/><circle cx="17" cy="9" r="2.5"/><path d="M17 14h1a4 4 0 0 1 4 4v1"/>',
  pickaxe: '<path d="m14 10-10 10"/><path d="M5 6c4-3.5 11-3 15 2-3-1.2-6-1-9 1"/><path d="m12.5 11.5 3.5 3.5"/>',
  shield: '<path d="M12 3 4 6v6c0 4.5 3.2 8 8 9 4.8-1 8-4.5 8-9V6z"/><path d="m9 12 2 2 4-4"/>',
  crosshair: '<circle cx="12" cy="12" r="7.5"/><path d="M12 2v5M12 17v5M2 12h5M17 12h5"/><circle cx="12" cy="12" r="1"/>',
  offerTag: '<path d="M3 12V4h8l10 10-8 8z"/><circle cx="7.5" cy="8.5" r="1.2"/>',

  chat: '<path d="M4 8h16M4 13h16M4 18h8"/>',

  externalLink: '<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  // Three equal bars — distinct from `chat`, whose last bar is deliberately short.
  menu: '<path d="M3 6h18M3 12h18M3 18h18"/>',
  close: '<path d="M5 5l14 14M19 5 5 19"/>',
  check: '<path d="m4 12.5 5 5L20 6.5"/>',
  skull: '<path d="M12 3c-4.4 0-8 3.1-8 7.2 0 2.3 1.1 4.2 2.8 5.5V19h10.4v-3.3c1.7-1.3 2.8-3.2 2.8-5.5C20 6.1 16.4 3 12 3z"/><circle cx="8.8" cy="10.5" r="1.6"/><circle cx="15.2" cy="10.5" r="1.6"/><path d="M10 19v2M14 19v2M12 13.5l-.8 1.7h1.6z"/>',
  crate: '<path d="M3 8l9-5 9 5v9l-9 5-9-5z"/><path d="M3 8l9 5 9-5M12 13v9"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>',
  // A document with a turned corner and a download arrow — the invoice row's
  // PDF link. Readable at 14px, which is the size it actually ships at.
  filePdf:
    '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M12 12v5"/><path d="m9.5 14.5 2.5 2.5 2.5-2.5"/>',

  download: '<path d="M12 4v11"/><path d="m7.5 11 4.5 4.5 4.5-4.5"/><path d="M5 20h14"/>',
  soundOn: '<path d="M11 5 6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/>',
  soundOff: '<path d="M11 5 6 9H3v6h3l5 4z"/><path d="m16 9 5 6M21 9l-5 6"/>',
};

/**
 * Inline SVG markup for `name`, sized relative to the current font size.
 *
 * Returns a string rather than an element because every screen in this app
 * builds its markup with template literals and `innerHTML`; handing back a node
 * would make every call site the odd one out.
 */
export function icon(name: IconName, className = ''): string {
  return (
    `<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" ` +
    `stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" data-icon="${name}" ` +
    `class="inline-block shrink-0${className ? ` ${className}` : ''}" aria-hidden="true" focusable="false">` +
    `${PATHS[name]}</svg>`
  );
}

/** Every icon this module can draw — lets tests assert the set stays complete. */
export const ICON_NAMES = Object.keys(PATHS) as IconName[];
